import SearchIcon from '@mui/icons-material/Search'
import SecurityIcon from '@mui/icons-material/Security'
import { Box, Chip, Divider, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import React, { useMemo, useState } from 'react'

interface PromptTemplate {
  label: string
  prompt: string
  category: 'search' | 'deals' | 'accounts' | 'reports' | 'versioning'
}

const PROMPT_TEMPLATES: PromptTemplate[] = [
  // Search & Lookup
  { label: 'Find an organization', prompt: 'Can you look up the organization "{name}"?', category: 'search' },
  { label: 'Account overview', prompt: 'Give me a full overview of {organization name} — active deals, products, and contacts.', category: 'search' },
  { label: 'Find a deal', prompt: 'What deals does {organization name} have?', category: 'search' },
  { label: 'Active deals summary', prompt: 'Show me all active deals for {organization name} with their products and pricing.', category: 'search' },

  // Deal Creation
  { label: 'Create new MSA', prompt: 'I need to create a new MSA for {organization name}.', category: 'deals' },
  { label: 'Create new IO', prompt: 'I need to create a new IO for {organization name}.', category: 'deals' },
  { label: 'Create new CSA', prompt: 'I need to create a new CSA for {organization name}.', category: 'deals' },
  { label: 'Add products to deal', prompt: 'Add the following products to the deal: {product names}', category: 'deals' },

  // Versioning
  { label: 'Price change (patch)', prompt: 'I need to update pricing on the active MSA for {organization name}.', category: 'versioning' },
  { label: 'Date renewal (minor)', prompt: 'I need to renew the dates on the active MSA for {organization name}.', category: 'versioning' },
  { label: 'Add/remove products (major)', prompt: 'I need to add new products to the active MSA for {organization name}. This will need a new contract.', category: 'versioning' },

  // Account Management
  { label: 'Update contact info', prompt: 'Update the primary contact for {organization name} to {contact name, email}.', category: 'accounts' },
  { label: 'Check contract status', prompt: 'What is the PandaDoc status for the latest contract for {organization name}?', category: 'accounts' },
  { label: 'List all contacts', prompt: 'Show me all contacts for {organization name}.', category: 'accounts' },

  // Reports
  { label: 'Revenue by org', prompt: 'What is the total monthly revenue for {organization name}?', category: 'reports' },
  { label: 'Media spend breakdown', prompt: 'Show me the media spend breakdown for {organization name}.', category: 'reports' },
  { label: 'Margin analysis', prompt: 'What are the margins on each product for {organization name}?', category: 'reports' },
]

const CATEGORY_LABELS: Record<string, { label: string; color: string }> = {
  search: { label: 'Search', color: '#42a5f5' },
  deals: { label: 'Deals', color: '#ffb703' },
  accounts: { label: 'Accounts', color: '#66bb6a' },
  reports: { label: 'Reports', color: '#ab47bc' },
  versioning: { label: 'Versioning', color: '#ef5350' },
}

interface PromptReferenceContentProps {
  onSelectPrompt?: (prompt: string) => void
}

const PromptReferenceContent: React.FC<PromptReferenceContentProps> = ({ onSelectPrompt }) => {
  const [search, setSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState<string | null>(null)

  const filtered = useMemo(() => {
    let items = PROMPT_TEMPLATES
    if (activeCategory) items = items.filter(p => p.category === activeCategory)
    if (search.trim()) {
      const q = search.toLowerCase()
      items = items.filter(p => p.label.toLowerCase().includes(q) || p.prompt.toLowerCase().includes(q))
    }
    return items
  }, [search, activeCategory])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <TextField
        placeholder="Search prompts..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        size="small"
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1,
          '& .MuiOutlinedInput-root': { fontSize: '0.8rem', bgcolor: 'background.default' },
        }}
      />

      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
        {Object.entries(CATEGORY_LABELS).map(([key, { label, color }]) => (
          <Chip
            key={key}
            label={label}
            size="small"
            variant={activeCategory === key ? 'filled' : 'outlined'}
            onClick={() => setActiveCategory(activeCategory === key ? null : key)}
            sx={{
              fontSize: '0.65rem',
              height: 22,
              cursor: 'pointer',
              borderColor: color,
              color: activeCategory === key ? '#fff' : color,
              bgcolor: activeCategory === key ? color : 'transparent',
              '&:hover': { bgcolor: `${color}22` },
            }}
          />
        ))}
      </Box>

      <Stack spacing={0.5} sx={{ overflowY: 'auto', flex: 1, pb: 1 }}>
        {filtered.map((p, idx) => {
          const catConfig = CATEGORY_LABELS[p.category]
          return (
            <Box
              key={idx}
              onClick={() => onSelectPrompt?.(p.prompt)}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: catConfig?.color || 'primary.main',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                  {p.label}
                </Typography>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: catConfig?.color || 'text.disabled',
                    flexShrink: 0,
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.3 }}>
                {p.prompt}
              </Typography>
            </Box>
          )
        })}
        {filtered.length === 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'center', mt: 2 }}>
            No prompts match your search
          </Typography>
        )}
      </Stack>

      <Divider sx={{ my: 1.5 }} />
      <Box sx={{ flexShrink: 0, px: 0.5, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          <SecurityIcon sx={{ fontSize: 14, color: 'success.main' }} />
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: 'success.main' }}>
            Safeguards in Place
          </Typography>
        </Box>
        <Stack spacing={0.5}>
          {[
            'All deal creation and modifications require explicit user confirmation before executing',
            'The agent must use search tools before answering — no fabricated responses',
            'Financial calculations are double-checked before presenting',
            'Multiple search variations are attempted before reporting "not found"',
            'Agent cites data sources from tool results, not from memory',
            'ALWAYS check agent output before confirming changes',
          ].map((text, i) => (
            <Typography key={i} variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.4, display: 'flex', gap: 0.5 }}>
              <span style={{ color: 'inherit', flexShrink: 0 }}>•</span>
              {text}
            </Typography>
          ))}
        </Stack>
      </Box>
    </Box>
  )
}

export default PromptReferenceContent
